import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
  Collapse,
  Paper,
  CircularProgress,
  withStyles,
} from '@material-ui/core'
import { Icon } from '@iconify/react'
import { mediaVideoStreamingUploadCancelAction } from '../../reducers/media-video/action'
import styles from './styles'

const GlobalUploadProgress = ({ classes, shouldShow = true }) => {
  const dispatch = useDispatch()
  const [hoveredUploadId, setHoveredUploadId] = useState(null)
  const streamingUploads = useSelector(
    state => state.mediaVideo.streamingUploads,
  )

  // Get all uploads (active and completed) - cancelled uploads are removed immediately
  const allUploads = Object.values(streamingUploads).filter(
    upload =>
      upload.status === 'uploading' ||
      upload.status === 'processing' ||
      upload.status === 'completed' ||
      upload.status === 'transcoding',
  )

  // Remove completed uploads after a short delay
  useEffect(() => {
    const completedUploads = allUploads.filter(
      upload => upload.status === 'completed',
    )
    if (completedUploads.length > 0) {
      const timers = completedUploads.map(upload =>
        setTimeout(() => {
          dispatch(mediaVideoStreamingUploadCancelAction(upload.id))
        }, 1500),
      )
      return () => {
        timers.forEach(timer => clearTimeout(timer))
      }
    }
  }, [allUploads, dispatch])

  // Cancel upload
  const handleCancelUpload = uploadId => {
    dispatch(mediaVideoStreamingUploadCancelAction(uploadId))
  }

  // Get progress info based on upload status
  const getProgressInfo = upload => {
    if (upload.status === 'uploading') {
      return {
        value: upload.progress || 0,
        color: '#015ffb',
        text: 'Uploading...',
        indeterminate: false,
      }
    } else if (upload.status === 'processing') {
      return {
        value: upload.progress || 0,
        color: '#4caf50',
        text: 'Processing...',
        indeterminate: false,
      }
    } else if (upload.status === 'transcoding') {
      return {
        value: 0,
        color: '#4caf50',
        text: 'Transcoding...',
        indeterminate: true,
      }
    } else if (upload.status === 'completed') {
      return {
        value: 100,
        color: '#4caf50',
        text: 'Completed',
        indeterminate: false,
      }
    } else if (upload.status === 'error') {
      return {
        value: 0,
        color: '#f44336',
        text: 'Error',
        indeterminate: false,
      }
    }
    return {
      value: 0,
      color: '#015ffb',
      text: 'Starting...',
      indeterminate: false,
    }
  }

  // Don't render if no uploads or if shouldShow is false
  if (!shouldShow || allUploads.length === 0) {
    return null
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.uploadsContainer}>
        {/* Show all uploads (active and completed) */}
        {allUploads.map(upload => (
          <Box key={upload.id} className={classes.activeUploadItem}>
            <Box className={classes.uploadRow}>
              <Box className={classes.uploadInfo}>
                <Typography variant="body2" noWrap className={classes.fileName}>
                  {upload.fileName}
                </Typography>

                <Typography
                  variant="caption"
                  color="textSecondary"
                  className={classes.statusText}
                >
                  {getProgressInfo(upload).text}
                </Typography>
              </Box>
              <Box
                className={classes.progressContainer}
                onMouseEnter={() => setHoveredUploadId(upload.id)}
                onMouseLeave={() => setHoveredUploadId(null)}
              >
                <Box
                  style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress
                    variant={
                      getProgressInfo(upload).indeterminate
                        ? 'indeterminate'
                        : 'determinate'
                    }
                    value={getProgressInfo(upload).value}
                    size={40}
                    thickness={3}
                    style={{ color: getProgressInfo(upload).color }}
                  />
                  {upload.status === 'completed' ? (
                    <Icon
                      icon="mdi:check"
                      style={{
                        position: 'absolute',
                        color: '#4caf50',
                        fontSize: '20px',
                      }}
                    />
                  ) : upload.status === 'error' ? (
                    <Icon
                      icon="mdi:alert-circle"
                      style={{
                        position: 'absolute',
                        color: '#f44336',
                        fontSize: '20px',
                      }}
                    />
                  ) : upload.status === 'transcoding' ? null : (
                    <>
                      {hoveredUploadId === upload.id ? (
                        <Icon
                          icon="mdi:close"
                          className={classes.icon}
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            cursor: 'pointer',
                            fontSize: '16px',
                          }}
                          onClick={() => handleCancelUpload(upload.id)}
                        />
                      ) : (
                        <Typography className={classes.progressText}>
                          {Math.round(getProgressInfo(upload).value)}%
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default withStyles(styles)(GlobalUploadProgress)
